import { t, Trans } from '@lingui/macro';
import { CarSpec, SALES_ITEM_TYPE, SalesItemForNewBooking } from 'noddi-types';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiButton, NoddiDialog } from 'noddi-ui-web';
import { compareLicensePlates, formatCurrencyAmount } from 'noddi-util';
import { commonTrackingEvents, tracking } from 'noddi-web';
import { useState } from 'react';
import { useBookingActions, useBookingSelectedSalesItemsCars } from '../bookingStore';
import { FleetCarSelectionButton } from '../Services/FleetCarSelectionButton';

export const CancelTireHotelOptionFleet = ({ option, cars }: { option: SalesItemForNewBooking; cars: CarSpec[] }) => {
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();
  const { addSalesItem, removeSalesItem, addSalesItems, removeSalesItems } = useBookingActions();

  const selectedCarsForOption = selectedSalesItemsCars.filter((item) => item.salesItemId === option.salesItemId);

  const hasAnySelectedSalesItems = !!selectedCarsForOption.length;

  const [isExpanded, setIsExpanded] = useState(hasAnySelectedSalesItems);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const isFree = option.grossPrice.amount === 0;
  const priceFormatted = isFree
    ? t`Free`
    : formatCurrencyAmount({
        money: option.grossPrice,
        decimals: 0
      });

  const onShowFullDescriptionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    tracking.track(commonTrackingEvents.salesItemDescriptionDetailsClicked, { salesItemName: option.name });
    setShowFullDescription(true);
  };

  function onExpandAndSelectAll(e: React.MouseEvent<HTMLButtonElement>) {
    const params = cars.map((car) => ({
      licensePlate: car.licensePlate,
      salesItemId: option.salesItemId,
      type: SALES_ITEM_TYPE.TIRE_HOTEL_CANCEL,
      isAddonsAvailable: option.isAddonsAvailable,
      isWheelPickupRequired: option.isWheelPickupRequired,
      mutuallyExclusiveSalesItemIds: option.mutuallyExclusiveSalesItemIds,
      bookingCategorySlug: option.bookingCategorySlug
    }));

    hasAnySelectedSalesItems ? removeSalesItems(params) : addSalesItems(params);
    setIsExpanded(!hasAnySelectedSalesItems);
    e.stopPropagation();
  }

  function onSelect({
    car,
    isAlreadySelected,
    event
  }: {
    car: CarSpec;
    isAlreadySelected: boolean;
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>;
  }): void {
    event.preventDefault();

    if (isAlreadySelected) {
      removeSalesItem({
        licensePlate: car.licensePlate,
        salesItemId: option.salesItemId,
        type: SALES_ITEM_TYPE.TIRE_HOTEL_CANCEL
      });
    } else {
      addSalesItem({
        licensePlate: car.licensePlate,
        salesItemId: option.salesItemId,
        type: SALES_ITEM_TYPE.TIRE_HOTEL_CANCEL,
        mutuallyExclusiveSalesItemIds: option.mutuallyExclusiveSalesItemIds,
        isAddonsAvailable: option.isAddonsAvailable,
        isWheelPickupRequired: option.isWheelPickupRequired,
        bookingCategorySlug: option.bookingCategorySlug
      });
    }
  }

  return (
    <NoddiBasicCard className={cn('flex flex-col gap-4')}>
      <NoddiDialog title={option.name} onClose={() => setShowFullDescription(false)} open={showFullDescription}>
        {option.description}
      </NoddiDialog>

      <div onClick={() => setIsExpanded((prev) => !prev)} className='flex flex-col gap-2 hover:cursor-pointer'>
        <div className='flex items-center justify-between gap-2'>
          <span className='text-xl'>{option.name}</span>
          <NoddiIcon name={isExpanded ? 'AltArrowUp' : 'AltArrowDown'} />
        </div>

        <p className={cn('font-semibold', isFree && 'text-success')}>{t`from` + ' ' + priceFormatted}</p>

        <p>{option.shortDescription}</p>

        <div className='flex h-12 items-end justify-end'>
          <div className='flex items-center gap-2'>
            <NoddiButton
              startIcon='ArrowSquareRightCorner'
              size='small'
              variant='link'
              onClick={onShowFullDescriptionClick}
            >
              <Trans>Details</Trans>
            </NoddiButton>
            <NoddiButton
              variant='secondary'
              size='small'
              startIcon={hasAnySelectedSalesItems ? 'Minus' : 'Plus'}
              onClick={onExpandAndSelectAll}
            >
              {hasAnySelectedSalesItems ? `${t`Remove`} (${selectedCarsForOption.length})` : t`Add`}
            </NoddiButton>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className='flex flex-col gap-2'>
          {cars.map((car) => {
            const isSelected = selectedSalesItemsCars.some(
              (item) =>
                compareLicensePlates(item.licensePlate, car.licensePlate) && item.salesItemId === option.salesItemId
            );

            return (
              <FleetCarSelectionButton
                key={car.licensePlate.number}
                isSelected={isSelected}
                onClick={(event) =>
                  onSelect({
                    car,
                    isAlreadySelected: isSelected,
                    event
                  })
                }
                make={car.make}
                model={car.model}
                licensePlateNumber={car.licensePlate.number}
                price={priceFormatted}
              />
            );
          })}
        </div>
      )}
    </NoddiBasicCard>
  );
};
